// The anchor card's template, card.anchor (contract §8.3): AnchorCardView
// (cloud/web/src/components/board/BoardCards.tsx) through the anchor status
// (§4.9): the distance from the anchor and the safe circle while the watch is
// on, the note, and the anchor watch's map in a live area (§5), which the CSS
// hides while the watch is off, as the card hides its map. Where the page
// differs: the distance and the safe circle stay, as "—", while the watch is
// off, since a status the page shows stays shown in every state (§9.6). The card is the
// status card with the anchor icon and the status word (§6.2).
//
// The note comes first in the markup and is ordered last on screen, so the
// row and the map can follow its state (`data-state="off"`). The row and the
// note hold their height whatever the status says, so the map stays put.

import { css, h, output } from "./kit.js";

const CSS = css(`
  .ac { gap: 12px; padding-top: 7px; }
  .ac-nt { order: 2; min-height: 1.35em; font: 400 13px/1.35 var(--tc-font-sans); color: var(--muted); }
  .ac-row { order: 1; min-height: 66px; display: flex; flex-wrap: wrap; align-items: flex-end; justify-content: space-between; gap: 12px; }
  .ac-row > div { display: flex; flex-direction: column; gap: 1px; }
  .ac-r { text-align: right; align-items: flex-end; }
  .ac-v { display: flex; align-items: baseline; gap: 4px; white-space: nowrap; }
  .ac-n { font: 700 30px/1.05 var(--tc-font-rounded); font-variant-numeric: tabular-nums;
    padding-block: 0.15em; margin-block: -0.15em; }
  .ac-hero .ac-n { font: 700 46px/1 var(--tc-font-rounded); }
  .ac-u { font-size: 15px; font-weight: 600; color: var(--text); }
  .ac-hero .ac-u { font-size: 18px; }
  .ac-n[data-quality=stale], .ac-n[data-quality=missing] { color: var(--muted); }
  /* At night a stale value keeps the text colour: grey under the night tint reads below the app's own values (§9.6, TOM-665). */
  html[data-theme=night] .ac-n[data-quality=stale] { color: var(--text); }
  .ac-cap { font: 400 13px/1.35 var(--tc-font-sans); color: var(--muted); }
  .ac tc-live { order: 3; width: 100%; height: 320px; flex: none; border-radius: 16px; overflow: hidden; }
  .ac-nt[data-state=off] ~ tc-live { display: none; }
`);

const status = (field, cls) => ({
  class: cls,
  "data-tc-status": "anchor",
  "data-tc-field": field,
});

/** card.anchor: the anchor watch's card (§8.3). */
export function cardAnchor(source) {
  const spec = source.spec;
  return output("card.anchor", 1, {
    html: h(
      "div",
      { class: "tp ac" },
      h("p", status("note", "ac-nt")),
      h(
        "div",
        { class: "ac-row" },
        h(
          "div",
          { class: "ac-hero" },
          h(
            "span",
            { class: "ac-v" },
            h("span", status("distance", "ac-n")),
            h("span", status("distance-unit", "ac-u")),
          ),
          h("p", { class: "ac-cap" }, "from the anchor"),
        ),
        h(
          "div",
          { class: "ac-r" },
          h("p", { class: "ac-cap" }, "Safe circle"),
          h(
            "span",
            { class: "ac-v" },
            h("span", status("circle", "ac-n")),
            h("span", status("circle-unit", "ac-u")),
          ),
        ),
      ),
      h("tc-live", { id: "map" }),
    ),
    css: CSS,
    live: [
      {
        id: "map",
        spec: { v: 1, kind: "anchor", boatId: spec.boatId },
        rolling: null,
      },
    ],
    card: {
      kind: "status",
      icon: "anchor",
      meta: { status: "anchor", field: "word" },
    },
  });
}
